import React, { ReactNode, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import cx from 'classnames';

import s from './BSelect.module.scss';

export type BSelectOptionType = {
  label: string;
  value: any;
  startAdornment?: ReactNode;
  [field: string]: any;
};

export interface BSelectProps<Option extends BSelectOptionType = BSelectOptionType> {
  className?: string;
  classNameButton?: string;
  options?: Option[];
  onChange?: (option: Option) => void;
  value?: any;
  placeholder?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  RenderValue?: ({ value }: {value: string}) => JSX.Element;
}

export const BSelect = (props: BSelectProps) => {
  const {
    options,
    className,
    classNameButton,
    placeholder,
    value,
    onChange,
    fullWidth,
    disabled,
    RenderValue,
    errorMessage,
  } = props;

  const [isActive, setIsActive] = useState(false);

  const selectRef = useRef(null);
  useClickAway(selectRef, () => setIsActive(false));

  const mods = {
    [s.opened]: isActive,
    [s.selected]: Boolean(value),
    [s.fullWidth]: fullWidth,
    [s.isErrorMessage]: Boolean(errorMessage),
    [s.isDisabled]: Boolean(disabled),
  };

  const currentOption = options?.find((option) => option?.value === value);

  const handleClickOnOption = (option: BSelectOptionType) => {
    if (onChange) {
      onChange(option);
    }

    setIsActive(false);
  };

  const handleToggle = () => {
    if (disabled) {
      return;
    }

    setIsActive((prevState) => !prevState);
  };

  return (
    <div className={cx(className, s.bSelect, mods)} ref={selectRef}>
      <div
        className={cx(classNameButton, s.bSelectButton)}
        onClick={handleToggle}
      >
        {currentOption?.startAdornment && (
          <span className={s.bSelectStartAdornment}>
            {currentOption.startAdornment}
          </span>
        )}
        <span className={s.bSelectLabel}>
          {
            RenderValue
              ? <RenderValue value={currentOption?.label || placeholder || ''} />
              : <span>{currentOption?.label || placeholder}</span>
          }
        </span>
        <span className={s.bSelectArrowIcon} />
      </div>
      {errorMessage && <span className={s.messageError}>{errorMessage}</span>}

      {isActive && (
        <div className={cx(s.bSelectContent)}>
          {options?.map((option) => (
            <div
              key={option.value + option.label}
              className={cx(s.bSelectOption)}
              onClick={() => handleClickOnOption(option)}
            >
              {option?.startAdornment && (
                <span className={s.bSelectStartAdornment}>
                  {option.startAdornment}
                </span>
              )}
              {
                RenderValue
                  ? <RenderValue value={option.label} />
                  : <span>{option.label}</span>
              }
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
